import { useNavigation } from '@react-navigation/native'
import { Text, Center, Box, Image, Button } from 'native-base'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, ListRenderItem, FlatList, FlatListProps } from 'react-native'

import { OfferTile } from './OfferTile'
import { Spacer } from './atoms/Spacer'

import { offerTileImgHeight } from '~constants'
import { useFilters } from '~hooks'
import { Images } from '~images'
import { OfferItem } from '~types/offer'

export const OffersList = ({
  offers,
  presentFilterBottomSheet,
  ...rest
}: { offers: OfferItem[]; presentFilterBottomSheet?(): void } & Omit<
  FlatListProps<OfferItem>,
  'renderItem' | 'data'
>) => {
  const { navigate } = useNavigation<OfferDetailsScreenProps['navigation']>()
  const { t } = useTranslation()
  const {
    filtersState: {
      categoriesList,
      coinRange: { from, to },
      selectedLocation,
      selectedOfferStatus,
    },
  } = useFilters()
  const ItemSeparatorComponent = useCallback(() => <Spacer y="2" />, [])

  const keyExtractor = useCallback(({ id }: OfferItem, index: number) => `${id}-${index}`, [])
  const getFlatListItemLayout = useCallback((_: unknown, i: number) => {
    return {
      length: offerTileImgHeight,
      offset: offerTileImgHeight * i,
      index: i,
    }
  }, [])
  const pressTileHandler = useCallback(
    (offerId: string) => {
      navigate('OfferDetails', { offerId })
    },
    [navigate]
  )

  const renderItem: ListRenderItem<OfferItem> = useCallback(
    ({ item }) => {
      const {
        buyStatus,
        discountCode,
        endDate,
        id: offerId,
        image,
        merchant,
        offerLeft,
        points,
        title,
      } = item

      const onTilePress = () => pressTileHandler(offerId)

      return (
        <Box px="4">
          <OfferTile
            key={offerId}
            // eslint-disable-next-line react/jsx-no-bind
            onPress={onTilePress}
            {...{ buyStatus, discountCode, endDate, image, merchant, offerLeft, points, title }}
          />
        </Box>
      )
    },
    [pressTileHandler]
  )
  const areSelectedFilters = useMemo(() => {
    const selectedCategories = categoriesList.filter(({ isChecked }) => isChecked === true).length
    return (
      !!from ||
      !!to ||
      !!selectedLocation.value ||
      !!selectedOfferStatus.value ||
      !!selectedCategories
    )
  }, [categoriesList, from, selectedLocation.value, selectedOfferStatus.value, to])

  return (
    <FlatList
      contentContainerStyle={styles.flashListContainer}
      data={offers || []}
      extraData={offers}
      getItemLayout={getFlatListItemLayout}
      ListEmptyComponent={
        <Center flexGrow={1} px="6">
          <Image alt="empty_list" source={Images.EMPTY_OFFER} width="50" height="50" />
          {areSelectedFilters && presentFilterBottomSheet ? (
            <>
              <Text bold mt="4" color="inactive" fontSize="md">
                {t('offers_list_screen.no_results_for_selected_filters')}
              </Text>
              <Spacer y="10" />
              <Button p="3" width="full" bg="actionBlue" onPress={presentFilterBottomSheet}>
                <Text bold color="white" fontSize="md">
                  {t('offers_list_screen.change_your_filters')}
                </Text>
              </Button>
            </>
          ) : (
            <>
              <Text bold mt="4" color="inactive" fontSize="md">
                {t('common.no_offers_available')}
              </Text>
            </>
          )}
        </Center>
      }
      onEndReachedThreshold={0.7}
      showsVerticalScrollIndicator={false}
      windowSize={5}
      {...{ ItemSeparatorComponent, keyExtractor, renderItem, ...rest }}
    />
  )
}

const styles = StyleSheet.create({
  flashListContainer: {
    flexGrow: 1,
    paddingVertical: 16,
  },
})
